import './FlyoutCard.css'
import './FlyoutForm.css'
import { useRef, useState, useEffect } from 'react';
import { getSessionToken } from '../assets/virtual-flyout/auth-service.js';
import { findAirportByName, getAirportDetails } from '../assets/virtual-flyout/airport-service.js';
import { fetch } from '../assets/virtual-flyout/waypont-service.js';
import PredictionTextField from './PredictionTextField.jsx';

import PropTypes from "prop-types";

FlyoutForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    flyout: PropTypes.object
}

function ImagePickerModal({ onClose, onSelect }) {
    const icons = [
        "flyout-plan.jpg",
        "flyout-4countries.jpg",
        "flyout-fjords.jpg",
        "flyout-night.jpg",
        "flyout-africa.jpg",
        "flyout-barnstormers.jpg",
        "flyout-tulips.jpg",
        "flyout-coldfront.jpg",
        "flyout-hills.jpg",
        "flyout-mountlake.jpg"
    ]

    return (
        <div className="flyout-form-modal-backdrop" onClick={onClose}>
            <div className="flyout-form-modal">
                <h3 className="flyout-form-modal-title">Choose the flyout picture</h3>
                <div className="flyout-form-modal-content">
                    {icons.map((icon, index) => (
                        <img className="flyout-card-image large" src={"/virtual-flyout/" + icon}
                        alt="Flyout picture" key={index} onClick={() => onSelect(icon)}/>
                    ))}
                </div>
            </div>
        </div>
    );
}

function LoginLinkModal({ link, onClose, onCopy }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        await onCopy();
        setCopied(true);
    };

    return (
        <div className="flyout-form-modal-backdrop">
            <div className="flyout-form-modal">
                <h3 className="flyout-form-modal-title">Login Link</h3>
                <div className="flyout-form-modal-content">
                    {link}
                </div>
                <div className="flyout-form-modal-buttons">
                    {copied ? (
                        <div className="flyout-form-button disabled">
                            Copied
                        </div>
                    ) : (
                        <button className="flyout-form-button" type="button" onClick={handleCopy}>
                            Copy to Clipboard
                        </button>
                    )}
                    <button className="flyout-form-button secondary" type="button" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export function FlyoutForm({ onSubmit, onCancel, flyout }) {
    const flyoutNameRef = useRef();
    const flyoutHighlightsRef = useRef();
    const flyoutDateRef = useRef();
    const departureRef = useRef();
    const arrivalRef = useRef();
    const windDirectionRef = useRef();
    const windSpeedRef = useRef();
    const temperatureRef = useRef();

    const [showImagePicker, setShowImagePicker] = useState(false);
    const [flyoutImage, setFlyoutImage] = useState(flyout?.image || "flyout-plan.jpg");

    const [errors, setErrors] = useState({});

    const [aircraftList, setAircraftList] = useState([
        { reg: '', type: 'P28A-161', PoB: 1 }
    ]);
    const [participantsList, setParticipantsList] = useState([]);
    const [loginLink, setLoginLink] = useState(null);

    useEffect(() => {
        if (flyout) {
            flyoutNameRef.current.value = flyout.name;
            flyoutHighlightsRef.current.value = flyout.highlights.join(', ');
            flyoutDateRef.current.value = flyout.days[0].date;
            departureRef.current.value = flyout.days[0].startPoint.code;
            arrivalRef.current.value = flyout.days[0].endPoint.code;
            windDirectionRef.current.value = flyout.weather.wind.heading;
            windSpeedRef.current.value = flyout.weather.wind.speed;
            temperatureRef.current.value = flyout.weather.temperature;
            setAircraftList(flyout.planes.map(plane => ({
                reg: plane.reg,
                type: plane.type,
                PoB: plane.PoB
            })));
            if (flyout.participants) {
                setParticipantsList(flyout.participants.map(participant => ({
                    name: participant.name,
                    email: participant.email
                })));
            }
        }
    }, [flyout]);


    function validate() {
        const errors = {};
        if (!flyoutNameRef.current.value) {
            errors.flyoutName = "Flyout name is required";
        }
        if (!flyoutDateRef.current.value) {
            errors.flyoutDate = "Flyout date is required";
        }
        if (!departureRef.current.value) {
            errors.departure = "Departure is required";
        }
        if (!arrivalRef.current.value) {
            errors.arrival = "Arrival is required";
        }
        if (!windDirectionRef.current.value || !windSpeedRef.current.value) {
            errors.wind = "Wind direction and speed are required";
        } else  {
            if (isNaN(parseInt(windDirectionRef.current.value)) || isNaN(parseInt(windSpeedRef.current.value))) {
                errors.wind = "Wind direction and speed must be numbers";
            } else {
                if (parseInt(windDirectionRef.current.value) < 0 || parseInt(windDirectionRef.current.value) > 360) {
                    errors.wind = "Wind direction must be between 0 and 360";
                }
                if (parseInt(windSpeedRef.current.value) < 0) {
                    errors.wind = "Wind speed must be greater than 0";
                }
            }
        }
        if (!temperatureRef.current.value) {
            errors.temperature = "Temperature is required";
        } else {
            if (isNaN(parseInt(temperatureRef.current.value))) {
                errors.temperature = "Temperature must be a number";
            }
        }
        return errors;
    }

    function handleSubmit(event) {
        event.preventDefault();
        
        const errors = validate();
        if (Object.keys(errors).length > 0) {
            setErrors(errors);
            return;
        }

        const newFlyout = {
            ...flyout,
            name: flyoutNameRef.current.value.toUpperCase(),
            image: flyoutImage,
            highlights: flyoutHighlightsRef.current.value.split(',').map(h => h.trim()),
            days: [{
                name: "Day 1",
                date: flyoutDateRef.current.value,
                startPoint: fetch(departureRef.current.value),
                endPoint: fetch(arrivalRef.current.value)
            }],
            weather: {
                wind: {
                    heading: parseInt(windDirectionRef.current.value),
                    speed: parseInt(windSpeedRef.current.value)
                },
                temperature: parseInt(temperatureRef.current.value)
            },
            planes: aircraftList.map(aircraft => ({
                reg: aircraft.reg.toUpperCase(),
                type: aircraft.type,
                PoB: parseInt(aircraft.PoB, 10)
            })),
            participants: participantsList.map(participant => ({
                name: participant.name,
                email: participant.email
            }))
        };
        onSubmit(newFlyout);
    }

    function handleIconSelect(icon) {
        setFlyoutImage(icon);
        setShowImagePicker(false);
    }

    function handleAddAircraft() {
        setAircraftList([...aircraftList, { reg: '', type: 'P28A-161', PoB: 1 }]);
    }

    function handleAircraftChange(index, field, value) {
        const newAircraftList = [...aircraftList];
        newAircraftList[index][field] = value;
        setAircraftList(newAircraftList);
    }

    function handleAddAircraft() {
        setAircraftList([...aircraftList, { reg: '', type: 'P28A-161', PoB: 1 }]);
    }

    function handleDeleteAircraft(index) {
        const newAircraftList = [...aircraftList];
        newAircraftList.splice(index, 1);
        setAircraftList(newAircraftList);
    }

    function handleAircraftChange(index, field, value) {
        const newAircraftList = [...aircraftList];
        newAircraftList[index][field] = value;
        setAircraftList(newAircraftList);
    }

    function handleAddParticipant() {
        setParticipantsList([...participantsList, { name: '', email: '' }]);
    }

    function handleDeleteParticipant(index) {
        const newParticipantsList = [...participantsList];
        newParticipantsList.splice(index, 1);
        setParticipantsList(newParticipantsList);
    }

    function handleParticipantChange(index, field, value) {
        const newParticipantsList = [...participantsList];
        newParticipantsList[index][field] = value;
        setParticipantsList(newParticipantsList);
    }

    async function handleGenerateLoginLink(index) {
        const baseUrl = window.location.origin;
        const email = participantsList[index].email;

        try {
            const sessionToken = await getSessionToken(email);
            const link = `${baseUrl}/login?sessionToken=${sessionToken}`;
            setLoginLink(link);
        } catch (error) {
            console.error('Failed to generate login link:', error);
            alert('Failed to generate login link. Please try again.');
        }
    }

    async function handleCopyLoginLink() {
        try {
            await navigator.clipboard.writeText(loginLink);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
        }
    }

    function formatAirport(airport) {
        return airport ? `${airport.airport_name} (${airport.airport_code})` : "";
    }

    function lookupAirport(searchValue) {
        const airport = findAirportByName(searchValue);
        return airport ? {
            displayValue: formatAirport(airport),
            value: airport.airport_code
        } : null;
    }

    function lookupAirportByCode(code) {
        if (!code) {
            return null;
        }
        const airport = getAirportDetails(code);
        return airport ? formatAirport(airport) : null;
    }

    return (
        <form onSubmit={handleSubmit}>
            <div className="flyout-card">
                <img className="flyout-card-image" src={"/virtual-flyout/" + flyoutImage}
                     alt="Flyout picture" onClick={() => setShowImagePicker(true)}/>
                <div className="flyout-card-details">
                    <input type="text" name="flyoutName" className="flyout-card-title" placeholder="NAME" autoFocus={true} ref={flyoutNameRef}/>
                    {errors.flyoutName && <div className="flyout-form-error">{errors.flyoutName}</div>}
                    <input type="text" name="flyoutHighlights" className="flyout-card-highlights" placeholder="Description" ref={flyoutHighlightsRef}/>
                    <div className="date-input-wrapper">
                        <input 
                            type="date" 
                            id="flyoutDate" 
                            name="flyoutDate" 
                            className="flyout-form-date-field"
                            ref={flyoutDateRef}
                        />
                        <svg 
                            className="date-input-icon"
                            xmlns="http://www.w3.org/2000/svg" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            strokeWidth="1" 
                            stroke="currentColor" 
                        >
                            <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z">

                            </path>
                        </svg>
                    </div>
                    {errors.flyoutDate && <div className="flyout-form-error">{errors.flyoutDate}</div>}
                </div>
            </div>
            <h3 className="flyout-form-param-group-title">Route</h3>
            <div className="flyout-form-param-grid">
                <div>Departure</div>
                <PredictionTextField 
                    ref={departureRef} 
                    lookupFn={lookupAirport}
                    displayFn={lookupAirportByCode}
                    className="flyout-form-field long caps"
                    placeholder="Airport name"
                />
                {errors.departure && <>
                    <span></span>
                    <div className="flyout-form-error">{errors.departure}</div>
                </>}
                <div>Arrival</div>
                <PredictionTextField 
                    ref={arrivalRef} 
                lookupFn={lookupAirport}
                displayFn={lookupAirportByCode}
                className="flyout-form-field long caps"
                    placeholder="Airport name"
                />
                {errors.arrival && <>
                    <span></span>
                    <div className="flyout-form-error">{errors.arrival}</div>
                </>}
            </div>
            <h3 className="flyout-form-param-group-title">Weather</h3>
            <div className="flyout-form-param-grid">
                <div>Mode</div>
                <div className="flyout-form-radio-group">
                    <input type="radio" id="simple" name="mode" value="Simple" checked={true} disabled={true}/>
                    <label htmlFor="simple">Simple</label>
                    <input type="radio" id="complex" name="mode" value="Complex" disabled={true}/>
                    <label htmlFor="complex">Complex</label>
                    <input type="radio" id="real-world" name="mode" value="Real World" disabled={true}/>
                    <label htmlFor="real-world">Real World</label>
                </div>
                <div>Wind</div>
                <div>
                    <input type="text" className="flyout-form-field" id="wind_direction" name="wind_direction" placeholder="HDG" ref={windDirectionRef}/> /
                    <input type="text" className="flyout-form-field" id="wind_speed" name="wind_speed" placeholder="SPD" ref={windSpeedRef}/> kts
                </div>
                {errors.wind && <>
                    <span></span>
                    <div className="flyout-form-error">{errors.wind}</div>
                </>}
                <div>Temp</div>
                <div>
                    <input type="text" className="flyout-form-field" id="temperature" name="temperature" placeholder="TEMP" ref={temperatureRef}/> °C at sea level
                </div>
                {errors.temperature && <>
                    <span></span>
                    <div className="flyout-form-error">{errors.temperature}</div>
                </>}
            </div>
            <h3 className="flyout-form-param-group-title">Aircraft</h3>
            <div className="flyout-form-param-list">
                {aircraftList.map((aircraft, index) => (
                    <div key={index} className="flyout-form-param-list-item">
                        <input type="text" className="flyout-form-field long caps" id={`aircraft_reg_${index}`} name={`aircraft_reg_${index}`} placeholder="G-ABCD" value={aircraft.reg} onChange={(e) => handleAircraftChange(index, 'reg', e.target.value)}/>
                        <select className="flyout-form-field long" id={`aircraft_type_${index}`} name={`aircraft_type_${index}`} value={aircraft.type} onChange={(e) => handleAircraftChange(index, 'type', e.target.value)}>
                            <option value="P28A-161">P28A-161</option>
                            <option value="P28A-181">P28A-181</option>
                            <option value="P28R-201">P28R-201</option>
                            <option value="P28U-201">P28U-201</option>
                        </select>
                        <input type="number" className="flyout-form-field" id={`aircraft_pob_${index}`} name={`aircraft_pob_${index}`} placeholder="PoB" min="1" max="4" step="1" value={aircraft.PoB} onChange={(e) => handleAircraftChange(index, 'PoB', e.target.value)}/>
                        <button className="flyout-form-side-button" type="button" onClick={() => handleDeleteAircraft(index)}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                ))}
                <button className="flyout-form-add-button" type="button" onClick={handleAddAircraft}>Add Aircraft</button>
            </div>

            <h3 className="flyout-form-param-group-title">Participants</h3>
            <div className="flyout-form-param-list">
                {participantsList.map((participant, index) => (
                    <div key={index} className="flyout-form-param-list-item">
                        <input type="text" 
                            className="flyout-form-field long" 
                            placeholder="Name" 
                            value={participant.name} 
                            onChange={(e) => handleParticipantChange(index, 'name', e.target.value)}
                        />
                        <input type="text" 
                            className="flyout-form-field flex-grow" 
                            placeholder="Email" 
                            value={participant.email} 
                            onChange={(e) => handleParticipantChange(index, 'email', e.target.value)}
                        />
                        <button 
                            className="flyout-form-side-button" 
                            type="button" 
                            onClick={() => handleGenerateLoginLink(index)} 
                            title="Generate login link">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
                            </svg>
                        </button>
                        <button 
                            className="flyout-form-side-button" 
                            type="button" 
                            onClick={() => handleDeleteParticipant(index)} 
                            title="Delete participant">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                ))}
                <button className="flyout-form-add-button" type="button" onClick={handleAddParticipant}>Add Person</button>
            </div>

            <div className="flyout-form-buttons">
                <button className="flyout-form-button" type="submit">Save</button>
                <button className="flyout-form-button secondary" onClick={onCancel}>Cancel</button>
            </div>

            {showImagePicker && (
                <ImagePickerModal
                    onClose={() => setShowImagePicker(false)}
                    onSelect={handleIconSelect}
                />
            )}  

            {loginLink && (
                <LoginLinkModal
                    link={loginLink}
                    onClose={() => setLoginLink(null)}
                    onCopy={handleCopyLoginLink}
                />
            )}
        </form>
    );
}
